import { useLanguage } from '../contexts/LanguageContext';
import { GearSix } from '@phosphor-icons/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { UsersTab, VolunteerFieldsTab, QuestionBankTab, ConfigQuestionnairesTab, ProtocolFieldsTab, TemplatesTab, ValidationTab } from '../components/settings/SettingsTabs';
import { IntegrationsTab } from '../components/settings/IntegrationsTab';
import { PortalConfigTab } from '../components/settings/PortalConfigTab';
import { PermissionsTab } from '../components/settings/PermissionsTab';
import { StudyConsentTemplatesPanel } from '../components/settings/StudyConsentTemplatesPanel';
import { SatisfactionAreasPanel } from '../components/settings/SatisfactionAreasPanel';
import { SatisfactionQuestionnairesPanel } from '../components/settings/SatisfactionQuestionnairesPanel';
import { DermScalesPanel } from '../components/settings/DermScalesPanel';
import CenterInfoPanel from '../components/settings/CenterInfoPanel';
import WorkingHoursPanel from '../components/settings/WorkingHoursPanel';
import AutoAssignmentRulesPanel from '../components/settings/AutoAssignmentRulesPanel';
import StatusesPanel from '../components/settings/StatusesPanel';
import UpcomingStudiesPanel from '../components/settings/UpcomingStudiesPanel';
import AppPromosPanel from '../components/settings/AppPromosPanel';
import NotificationRulesPanel from '../components/settings/NotificationRulesPanel';
import DelegationCatalogPanel from '../components/settings/DelegationCatalogPanel';

export default function Settings() {
  const { t, lang } = useLanguage();
  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center gap-3">
        <GearSix size={28} weight="duotone" className="text-[#1f3e80]" />
        <h1 className="text-3xl font-heading font-semibold tracking-tight text-stone-900">{t('settings')}</h1>
      </div>
      <Tabs defaultValue="users" className="w-full">
        <TabsList className="bg-stone-100">
          <TabsTrigger value="users" data-testid="settings-tab-users">{lang === 'es' ? 'Usuarios' : 'Users'}</TabsTrigger>
          <TabsTrigger value="volunteers" data-testid="settings-tab-volunteers">{lang === 'es' ? 'Voluntarios' : 'Volunteers'}</TabsTrigger>
          <TabsTrigger value="questions" data-testid="settings-tab-questions">{lang === 'es' ? 'Cuestionarios' : 'Questionnaires'}</TabsTrigger>
          <TabsTrigger value="templates" data-testid="settings-tab-templates">{lang === 'es' ? 'Plantillas' : 'Templates'}</TabsTrigger>
          <TabsTrigger value="protocol" data-testid="settings-tab-protocol">{lang === 'es' ? 'Protocolo' : 'Protocol'}</TabsTrigger>
          <TabsTrigger value="studies" data-testid="settings-tab-studies">{lang === 'es' ? 'Estudios' : 'Studies'}</TabsTrigger>
          <TabsTrigger value="validation" data-testid="settings-tab-validation">{lang === 'es' ? 'Validacion' : 'Validation'}</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="settings-tab-integrations">{lang === 'es' ? 'Integraciones' : 'Integrations'}</TabsTrigger>
          <TabsTrigger value="portal" data-testid="settings-tab-portal">{lang === 'es' ? 'Portal' : 'Portal'}</TabsTrigger>
          <TabsTrigger value="center" data-testid="settings-tab-center">{lang === 'es' ? 'Centro' : 'Center'}</TabsTrigger>
          <TabsTrigger value="permissions" data-testid="settings-tab-permissions">{lang === 'es' ? 'Permisos' : 'Permissions'}</TabsTrigger>
          <TabsTrigger value="delegation" data-testid="settings-tab-delegation">{lang === 'es' ? 'Delegacion' : 'Delegation'}</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="settings-tab-notifications">{lang === 'es' ? 'Avisos' : 'Alerts'}</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-6"><UsersTab lang={lang} /></TabsContent>
        <TabsContent value="volunteers" className="mt-6"><VolunteerFieldsTab lang={lang} /></TabsContent>
        <TabsContent value="questions" className="mt-6">
          <Tabs defaultValue="config-questionnaires" className="w-full">
            <TabsList className="bg-stone-50 border border-stone-200">
              <TabsTrigger value="config-questionnaires" data-testid="subtab-questionnaires">{lang === 'es' ? 'Cuestionarios' : 'Questionnaires'}</TabsTrigger>
              <TabsTrigger value="question-bank" data-testid="subtab-question-bank">{lang === 'es' ? 'Banco de Preguntas' : 'Question Bank'}</TabsTrigger>
              <TabsTrigger value="derm-scales" data-testid="subtab-derm-scales">{lang === 'es' ? 'Escalas dermatológicas' : 'Dermatological scales'}</TabsTrigger>
              <TabsTrigger value="satisfaction-q" data-testid="subtab-satisfaction-q">{lang === 'es' ? 'Satisfacción' : 'Satisfaction'}</TabsTrigger>
              <TabsTrigger value="satisfaction-areas" data-testid="subtab-satisfaction-areas">{lang === 'es' ? 'Áreas funcionales' : 'Functional areas'}</TabsTrigger>
            </TabsList>
            <TabsContent value="config-questionnaires" className="mt-4"><ConfigQuestionnairesTab lang={lang} /></TabsContent>
            <TabsContent value="question-bank" className="mt-4"><QuestionBankTab lang={lang} /></TabsContent>
            <TabsContent value="derm-scales" className="mt-4"><DermScalesPanel lang={lang} /></TabsContent>
            <TabsContent value="satisfaction-q" className="mt-4"><SatisfactionQuestionnairesPanel lang={lang} /></TabsContent>
            <TabsContent value="satisfaction-areas" className="mt-4"><SatisfactionAreasPanel lang={lang} /></TabsContent>
          </Tabs>
        </TabsContent>
        <TabsContent value="templates" className="mt-6">
          <Tabs defaultValue="volunteer-templates" className="w-full">
            <TabsList className="bg-stone-50 border border-stone-200">
              <TabsTrigger value="volunteer-templates" data-testid="subtab-volunteer-templates">{lang === 'es' ? 'LOPD / Imagenes (voluntario)' : 'LOPD / Images (volunteer)'}</TabsTrigger>
              <TabsTrigger value="study-ci" data-testid="subtab-study-ci">{lang === 'es' ? 'CI de Estudio' : 'Study CI'}</TabsTrigger>
            </TabsList>
            <TabsContent value="volunteer-templates" className="mt-4"><TemplatesTab lang={lang} /></TabsContent>
            <TabsContent value="study-ci" className="mt-4"><StudyConsentTemplatesPanel lang={lang} /></TabsContent>
          </Tabs>
        </TabsContent>
        <TabsContent value="protocol" className="mt-6"><ProtocolFieldsTab lang={lang} /></TabsContent>
        <TabsContent value="studies" className="mt-6">
          <Tabs defaultValue="auto-assignment" className="w-full">
            <TabsList className="bg-stone-50 border border-stone-200">
              <TabsTrigger value="auto-assignment" data-testid="subtab-auto-assignment">
                {lang === 'es' ? 'Reglas de Autoasignación' : 'Auto-assignment rules'}
              </TabsTrigger>
              <TabsTrigger value="study-statuses" data-testid="subtab-study-statuses">
                {lang === 'es' ? 'Estado Estudio' : 'Study status'}
              </TabsTrigger>
              <TabsTrigger value="appointment-statuses" data-testid="subtab-appointment-statuses">
                {lang === 'es' ? 'Estado Cita' : 'Appointment status'}
              </TabsTrigger>
              <TabsTrigger value="upcoming-studies" data-testid="subtab-upcoming-studies">
                {lang === 'es' ? 'Nuevos Estudios' : 'Upcoming Studies'}
              </TabsTrigger>
              <TabsTrigger value="app-promos" data-testid="subtab-app-promos">
                {lang === 'es' ? 'Publicidad App' : 'App Ads'}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="auto-assignment" className="mt-4">
              <AutoAssignmentRulesPanel lang={lang} />
            </TabsContent>
            <TabsContent value="study-statuses" className="mt-4">
              <StatusesPanel lang={lang} endpoint="study-statuses" entity="study" />
            </TabsContent>
            <TabsContent value="appointment-statuses" className="mt-4">
              <StatusesPanel lang={lang} endpoint="appointment-statuses" entity="appointment" />
            </TabsContent>
            <TabsContent value="upcoming-studies" className="mt-4">
              <UpcomingStudiesPanel lang={lang} />
            </TabsContent>
            <TabsContent value="app-promos" className="mt-4">
              <AppPromosPanel lang={lang} />
            </TabsContent>
          </Tabs>
        </TabsContent>
        <TabsContent value="validation" className="mt-6"><ValidationTab lang={lang} /></TabsContent>
        <TabsContent value="integrations" className="mt-6"><IntegrationsTab lang={lang} /></TabsContent>
        <TabsContent value="portal" className="mt-6"><PortalConfigTab lang={lang} /></TabsContent>
        <TabsContent value="center" className="mt-6">
          <Tabs defaultValue="info" className="w-full">
            <TabsList className="bg-stone-50 border border-stone-200">
              <TabsTrigger value="info" data-testid="subtab-center-info">
                {lang === 'es' ? 'Información' : 'Info'}
              </TabsTrigger>
              <TabsTrigger value="hours" data-testid="subtab-working-hours">
                {lang === 'es' ? 'Horario laboral' : 'Working hours'}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="info" className="mt-4"><CenterInfoPanel lang={lang} /></TabsContent>
            <TabsContent value="hours" className="mt-4"><WorkingHoursPanel lang={lang} /></TabsContent>
          </Tabs>
        </TabsContent>
        <TabsContent value="permissions" className="mt-6"><PermissionsTab lang={lang} /></TabsContent>
        <TabsContent value="delegation" className="mt-6"><DelegationCatalogPanel lang={lang} /></TabsContent>
        <TabsContent value="notifications" className="mt-6"><NotificationRulesPanel lang={lang} /></TabsContent>
      </Tabs>
    </div>
  );
}
